import * as THREE from 'three'
import type { GSplatSogsData } from '@/plugins/gsplat/core/GSplatSogsData'
import {
  getGSplatBatchInstanceCount,
  setBatchedSplatQuadGeometry,
} from '@/plugins/gsplat/core/gsplatBatching'
import { GaussianSplatMaterial } from '@/plugins/gsplat/materials/GaussianSplatMaterial'
import { type SorterOptions, SplatSorter } from '@/plugins/gsplat/sorting/SplatSorter'
import { getRendererViewportDrawingBufferSize } from '@/plugins/gsplat/utils/getRendererViewportSize'
import { getTextureImage } from '@/plugins/gsplat/utils/textureImage'
import { GSPLAT_SORT_DIRECTION_THRESHOLD, GSPLAT_SORT_POSITION_THRESHOLD } from '@/shared/constants'
import { getLoggerManager } from '@/shared/logging'
import type { GSplatShaderEffectLike } from '@/shared/types/gsplat'
import { beginDisposeOnce } from '@/shared/utils/disposeOnce'
import type { ColorSpaceManager } from './ColorSpaceManager'

const log = getLoggerManager().getLogger('gsplat:core')

export interface GaussianSogsMeshOptions {
  depthWrite?: boolean
  depthTest?: boolean
  transparent?: boolean
  enableSH?: boolean
  maxSHBands?: number
  enableSorting?: boolean
  sortingOptions?: SorterOptions
  customAabb?: THREE.Box3
  centers?: Float32Array
}

class GaussianSogsGeometry extends THREE.InstancedBufferGeometry {
  readonly orderTexture: THREE.DataTexture
  readonly orderWidth: number
  readonly orderHeight: number
  visibleSplatCount: number = 0

  constructor(numSplats: number) {
    super()

    setBatchedSplatQuadGeometry(this)
    this.setVisibleSplatCount(numSplats)

    const [orderWidth, orderHeight] = this.evalTextureSize(numSplats)
    this.orderWidth = orderWidth
    this.orderHeight = orderHeight

    const orderData = new Uint32Array(orderWidth * orderHeight)
    for (let i = 0; i < numSplats; i++) {
      orderData[i] = i
    }

    this.orderTexture = new THREE.DataTexture(
      orderData,
      orderWidth,
      orderHeight,
      THREE.RedIntegerFormat,
      THREE.UnsignedIntType
    )
    this.orderTexture.internalFormat = 'R32UI'
    this.orderTexture.minFilter = THREE.NearestFilter
    this.orderTexture.magFilter = THREE.NearestFilter
    this.orderTexture.wrapS = THREE.ClampToEdgeWrapping
    this.orderTexture.wrapT = THREE.ClampToEdgeWrapping
    this.orderTexture.needsUpdate = true
  }

  setVisibleSplatCount(count: number): void {
    this.visibleSplatCount = Math.max(0, Math.floor(count))
    this.instanceCount = getGSplatBatchInstanceCount(this.visibleSplatCount)
  }

  updateOrder(sortedIndices: Uint32Array): void {
    if (sortedIndices.length !== this.visibleSplatCount) {
      log.error('Sorted indices length mismatch')
      return
    }

    const data = this.orderTexture.image.data as Uint32Array
    data.set(sortedIndices)
    this.orderTexture.needsUpdate = true
  }

  override dispose(): void {
    this.orderTexture.dispose()
    super.dispose()
  }

  private evalTextureSize(count: number): [number, number] {
    const width = Math.ceil(Math.sqrt(count))
    const height = Math.ceil(count / width)
    return [width, height]
  }
}

export class GaussianSogsMesh extends THREE.Mesh<GaussianSogsGeometry, GaussianSplatMaterial> {
  readonly splatData: GSplatSogsData
  readonly dataWidth: number
  readonly dataHeight: number

  private sorter: SplatSorter | null = null
  private colorSpaceManager: ColorSpaceManager | null = null
  private _shaderEffect: GSplatShaderEffectLike | null = null
  private codebookTextures: THREE.DataTexture[] = []
  private centers: Float32Array | null = null
  private lastSortCameraPosition = new THREE.Vector3(Infinity, Infinity, Infinity)
  private lastSortCameraDirection = new THREE.Vector3(Infinity, Infinity, Infinity)
  private drawingBufferSize = new THREE.Vector2()
  private boundingBoxCenter = new THREE.Vector3()
  private boundingBoxSize = new THREE.Vector3()
  private currentSortCameraDirection = new THREE.Vector3()

  private static readonly SORT_POSITION_THRESHOLD = GSPLAT_SORT_POSITION_THRESHOLD
  private static readonly SORT_DIRECTION_THRESHOLD = GSPLAT_SORT_DIRECTION_THRESHOLD

  constructor(splatData: GSplatSogsData, options: GaussianSogsMeshOptions = {}) {
    const geometry = new GaussianSogsGeometry(splatData.numSplats)
    const shBands = splatData.shBands
    const effectiveEnableSH = shBands > 0 ? (options.enableSH ?? true) : false
    const effectiveMaxSHBands = effectiveEnableSH ? (options.maxSHBands ?? shBands) : 0

    const material = new GaussianSplatMaterial({
      depthWrite: options.depthWrite,
      depthTest: options.depthTest,
      transparent: options.transparent,
      enableSH: effectiveEnableSH,
      maxSHBands: effectiveMaxSHBands,
    })

    super(geometry, material)

    this.splatData = splatData
    this.centers = options.centers ?? null
    const meansLowImage = getTextureImage(splatData.means_l)
    this.dataWidth = Math.floor(meansLowImage.width)
    this.dataHeight = Math.floor(meansLowImage.height)

    material.uniforms.splatOrder.value = geometry.orderTexture
    material.setMetadata(splatData.numSplats, this.dataWidth, geometry.orderWidth)
    material.setSogsTextures(
      {
        meansLow: splatData.means_l,
        meansHigh: splatData.means_u,
        quats: splatData.quats,
        scales: splatData.scales,
        sh0: splatData.sh0,
        scalesCodebook: this.createCodebookTexture(
          'sogsScalesCodebook',
          splatData.meta.scales.codebook
        ),
        sh0Codebook: this.createCodebookTexture('sogsSH0Codebook', splatData.meta.sh0.codebook),
      },
      splatData.meta
    )

    const shRange = this.getShRange()
    material.setSogsSHTextures(
      effectiveEnableSH ? (splatData.sh_centroids ?? null) : null,
      effectiveEnableSH ? (splatData.sh_labels ?? null) : null,
      shRange.min,
      shRange.max,
      effectiveMaxSHBands
    )

    if (options.customAabb) {
      geometry.boundingBox = options.customAabb.clone()
    } else {
      const aabb = new THREE.Box3()
      splatData.calcAabb(aabb)
      geometry.boundingBox = aabb
    }
    this.updateBoundingSphereFromBox()

    if (options.enableSorting !== false && this.centers) {
      geometry.setVisibleSplatCount(0)
      this.sorter = new SplatSorter(
        {
          numSplats: splatData.numSplats,
          getCenters: () => this.centers!,
        },
        geometry.orderTexture,
        {
          ...options.sortingOptions,
          retainCentersForMapping: false,
          onSortComplete: (data) => {
            this.geometry.setVisibleSplatCount(data.count)
            this.dispatchEvent({ type: 'sortComplete', ...data })
          },
        }
      )
      this.centers = null
    }
  }

  onBeforeRender = (
    renderer: THREE.WebGLRenderer,
    _scene: THREE.Scene,
    camera: THREE.Camera
  ): void => {
    const size = getRendererViewportDrawingBufferSize(renderer, this.drawingBufferSize)
    this.material.setViewport(size.x, size.y)
    this.material.setCameraParams(camera)

    if (this.colorSpaceManager) {
      this.colorSpaceManager.configureMaterial(this.material)
    } else {
      this.material.updateGammaMode(renderer)
    }

    if (this.sorter && this.checkCameraChanged(camera)) {
      this.sorter.setCamera(camera, this.matrixWorld)
    }
  }

  setColorSpaceManager(manager: ColorSpaceManager): void {
    this.colorSpaceManager = manager
    manager.configureMaterial(this.material)
  }

  getColorSpaceManager(): ColorSpaceManager | null {
    return this.colorSpaceManager
  }

  updateOrder(sortedIndices: Uint32Array): void {
    this.geometry.updateOrder(sortedIndices)
  }

  setShaderEffect(effect: GSplatShaderEffectLike | null): boolean {
    if (this._shaderEffect === effect) {
      return effect ? effect.enable(this.material) : true
    }

    this._shaderEffect?.disable()
    this._shaderEffect = effect

    if (!effect) {
      this.material.clearShaderEffect()
      return true
    }

    return effect.enable(this.material)
  }

  clearShaderEffect(): boolean {
    return this.setShaderEffect(null)
  }

  restartShaderEffect(): boolean {
    return this._shaderEffect?.restart() ?? false
  }

  getShaderEffect(): GSplatShaderEffectLike | null {
    return this._shaderEffect
  }

  updateShaderEffect(delta: number): void {
    this._shaderEffect?.update(delta)
  }

  dispose(): void {
    if (!beginDisposeOnce(this)) return

    // Three r186 起 Object3D 才提供 dispose()，仅用于派发 dispose 事件；旧 peer 无该方法，必须可选调用。
    super.dispose?.()
    this._shaderEffect?.destroy()
    this._shaderEffect = null
    this.sorter?.dispose()
    this.sorter = null
    this.centers = null

    for (const texture of this.codebookTextures) {
      texture.dispose()
    }
    this.codebookTextures = []

    this.splatData.destroy()
    this.geometry.dispose()
    this.material.dispose()
  }

  private createCodebookTexture(
    name: string,
    codebook?: Array<number | null | undefined>
  ): THREE.DataTexture | undefined {
    if (!codebook) {
      return undefined
    }

    const data = new Float32Array(256)
    for (let i = 0; i < 256; i++) {
      data[i] = codebook[i] ?? 0
    }

    const texture = new THREE.DataTexture(data, 256, 1, THREE.RedFormat, THREE.FloatType)
    texture.name = name
    texture.internalFormat = 'R32F'
    texture.minFilter = THREE.NearestFilter
    texture.magFilter = THREE.NearestFilter
    texture.wrapS = THREE.ClampToEdgeWrapping
    texture.wrapT = THREE.ClampToEdgeWrapping
    texture.generateMipmaps = false
    texture.needsUpdate = true

    this.codebookTextures.push(texture)
    return texture
  }

  private getShRange(): { min: number; max: number } {
    const shN = this.splatData.meta.shN

    if (this.splatData.meta.version === 2 && shN?.codebook) {
      return {
        min: shN.codebook[0] ?? 0,
        max: shN.codebook[255] ?? shN.codebook[shN.codebook.length - 1] ?? 1,
      }
    }

    return {
      min: shN?.mins ?? 0,
      max: shN?.maxs ?? 1,
    }
  }

  private updateBoundingSphereFromBox(): void {
    const box = this.geometry.boundingBox
    if (!box) {
      return
    }

    const center = box.getCenter(this.boundingBoxCenter)
    const size = box.getSize(this.boundingBoxSize)
    const radius = size.length() / 2
    if (!this.geometry.boundingSphere) {
      this.geometry.boundingSphere = new THREE.Sphere()
    }
    this.geometry.boundingSphere.center.copy(center)
    this.geometry.boundingSphere.radius = radius
  }

  private checkCameraChanged(camera: THREE.Camera): boolean {
    const pos = camera.position
    const dir = camera.getWorldDirection(this.currentSortCameraDirection)
    const posDelta = pos.distanceToSquared(this.lastSortCameraPosition)
    const posThreshold =
      GaussianSogsMesh.SORT_POSITION_THRESHOLD * GaussianSogsMesh.SORT_POSITION_THRESHOLD
    const dirDot = dir.dot(this.lastSortCameraDirection)
    const dirChanged = dirDot < 1.0 - GaussianSogsMesh.SORT_DIRECTION_THRESHOLD

    if (posDelta > posThreshold || dirChanged) {
      this.lastSortCameraPosition.copy(pos)
      this.lastSortCameraDirection.copy(dir)
      return true
    }

    return false
  }
}
